import React from 'react';
import Svg, { Path } from 'react-native-svg';

/**
 * CV preview bottom-bar icons, exported 1:1 from Figma node 3062-6178
 * (frames 3147:712 / :717 / :722 / :727 / :733). All four outline glyphs share
 * viewBox 0 0 18 18, 1.5px stroke, round caps/joins. The Enhance action uses a
 * glossy 3D sparkle PNG (assets/branding/upgrade-spark.png) rendered directly
 * by the bottom bar.
 *
 * The raw path strings are also exported so the DOM-based `.web.tsx` bar can
 * render identical glyphs without pulling in react-native-svg.
 */

export const CV_BAR_ICON_PATHS = {
  cvs: [
    'M4.18 14.75H3.75C2.645 14.75 1.75 13.855 1.75 12.75V3.25C1.75 2.698 2.198 2.25 2.75 2.25H6.547C6.835 2.25 7.11 2.375 7.3 2.592L9.625 5.25H15.251',
    'M16.1868 7.75H5.30781C4.83581 7.75 4.42881 8.079 4.32981 8.54L3.25881 13.54C3.12581 14.163 3.59981 14.75 4.23681 14.75H15.1148C15.5868 14.75 15.9938 14.421 16.0928 13.96L17.1638 8.96C17.2968 8.337 16.8238 7.75 16.1868 7.75Z',
  ],
  edit: [
    'M2.75 15.25C2.75 15.25 6.34901 14.682 7.29601 13.735C8.24301 12.788 14.623 6.40799 14.623 6.40799C15.46 5.57099 15.46 4.21399 14.623 3.37799C13.786 2.54099 12.429 2.54099 11.593 3.37799C11.593 3.37799 5.21301 9.75799 4.26601 10.705C3.31901 11.652 2.75101 15.251 2.75101 15.251L2.75 15.25Z',
    'M10 15.25H15.25',
  ],
  theme: [
    'M6.95605 9.04399L13.5341 2.46599C14.1551 1.84499 15.1631 1.84499 15.7841 2.46599C16.4051 3.08699 16.4051 4.09499 15.7841 4.71599L9.20606 11.294',
    'M1.75 14.706C4.453 15.518 6.646 15.586 8.439 13.751C9.52 12.666 9.52 10.906 8.439 9.82002C7.358 8.73402 5.613 8.71802 4.523 9.82002C2.75 11.612 4.298 13.314 1.75 14.706Z',
  ],
  analytics: [
    'M1.75 9C1.75 9 4.15 4.25 9 4.25C13.85 4.25 16.25 9 16.25 9C16.25 9 13.85 13.75 9 13.75C4.15 13.75 1.75 9 1.75 9Z',
    'M9 11.25C10.2426 11.25 11.25 10.2426 11.25 9C11.25 7.75736 10.2426 6.75 9 6.75C7.75736 6.75 6.75 7.75736 6.75 9C6.75 10.2426 7.75736 11.25 9 11.25Z',
  ],
} as const;

/** Exact Figma stroke color for the four outline glyphs and their labels. */
export const CV_BAR_ICON_COLOR = '#7D7C81';

interface IconProps {
  size?: number;
  color?: string;
  strokeWidth?: number;
}

function OutlineIcon({
  paths,
  size = 18,
  color = CV_BAR_ICON_COLOR,
  strokeWidth = 1.5,
}: IconProps & { paths: readonly string[] }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      {paths.map((d) => (
        <Path
          key={d}
          d={d}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </Svg>
  );
}

export function CvsIcon(props: IconProps) {
  return <OutlineIcon paths={CV_BAR_ICON_PATHS.cvs} {...props} />;
}

export function EditIcon(props: IconProps) {
  return <OutlineIcon paths={CV_BAR_ICON_PATHS.edit} {...props} />;
}

export function ThemeIcon(props: IconProps) {
  return <OutlineIcon paths={CV_BAR_ICON_PATHS.theme} {...props} />;
}

export function AnalyticsIcon(props: IconProps) {
  return <OutlineIcon paths={CV_BAR_ICON_PATHS.analytics} {...props} />;
}
